import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Menu,
  Search,
  Bell,
  Sun,
  Moon,
  LogOut,
  Settings,
  ChevronDown,
  Globe,
} from 'lucide-react';
import { cn } from '@/shared/utilities/cn';
import { useAuth } from '@/shared/auth';
import { useTheme } from '@/shared/utilities';
import { Button } from '@/shared/components/ui';
import { TenantSelector } from './TenantSelector';

interface HeaderProps {
  onMenuClick?: () => void;
  className?: string;
}

export function Header({ onMenuClick, className }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { setTheme, resolvedTheme } = useTheme();
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'ar' ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
  };

  return (
    <header
      className={cn(
        'flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4',
        'dark:border-slate-800 dark:bg-slate-900',
        className
      )}
    >
      {/* Left side - Menu button and tenant selector */}
      <div className="flex items-center gap-4">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onMenuClick}
          className="lg:hidden"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Tenant selector */}
        <TenantSelector />
      </div>

      {/* Right side - Search, notifications, user menu */}
      <div className="flex items-center gap-2">
        {/* Global search placeholder */}
        <Button variant="ghost" size="sm" className="hidden md:flex" aria-label={t('common.search')}>
          <Search className="h-4 w-4" />
        </Button>

        {/* Notifications placeholder */}
        <Button variant="ghost" size="sm" className="relative" aria-label="Notifications">
          <Bell className="h-4 w-4" />
          <span className="absolute -top-0.5 -end-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-error-500 text-[8px] font-bold text-white">
            3
          </span>
        </Button>

        {/* Language toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleLanguage}
          aria-label="Toggle language"
        >
          <Globe className="h-4 w-4" />
          <span className="ms-1 text-xs font-medium">
            {i18n.language === 'ar' ? 'EN' : 'ع'}
          </span>
        </Button>

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          {resolvedTheme === 'dark' ? (
            <Sun className="h-4 w-4" />
          ) : (
            <Moon className="h-4 w-4" />
          )}
        </Button>

        {/* User menu */}
        <div className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="gap-2"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-sm font-medium text-primary-700 dark:bg-primary-900 dark:text-primary-300">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <span className="hidden text-sm font-medium md:inline">
              {user?.name || 'User'}
            </span>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </Button>

          {/* Dropdown menu */}
          {userMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setUserMenuOpen(false)}
              />
              <div className="absolute end-0 z-50 mt-2 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800">
                <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-700">
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {user?.name}
                  </p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                  <span className="mt-1 inline-block rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700 dark:bg-primary-900 dark:text-primary-300">
                    {user?.role?.replace('_', ' ')}
                  </span>
                </div>
                <a
                  href="/app/settings"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700"
                  onClick={() => setUserMenuOpen(false)}
                >
                  <Settings className="h-4 w-4" />
                  {t('nav.settings')}
                </a>
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-error-600 hover:bg-slate-100 dark:text-error-400 dark:hover:bg-slate-700"
                >
                  <LogOut className="h-4 w-4" />
                  {t('auth.logout')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
